import type {Plugin} from 'vite';
import {parseAst} from 'vite';
import MagicString from 'magic-string';
import type {OptionsContext} from './context.js';

type Node = Record<string, unknown> & {
  type: string;
  start: number;
  end: number;
};

/** One class body that declares at least one private name. */
interface ClassScope {
  /** Private name (without `#`) to the identifier of its symbol constant. */
  names: Map<string, string>;
}

const PRIVATE_KEY_HOLDERS = new Set([
  'PropertyDefinition',
  'MethodDefinition',
  'AccessorProperty',
]);

const isNode = (v: unknown): v is Node =>
  v !== null && typeof v === 'object' && typeof (v as Node).type === 'string';

/** Whether a class member is keyed by a `#private` name. */
const isPrivateKey = (member: Node): boolean =>
  PRIVATE_KEY_HOLDERS.has(member.type) &&
  isNode(member.key) &&
  member.key.type === 'PrivateIdentifier';

/** Private names declared directly in a class body. */
const declaredNames = (cls: Node): Node[] => {
  const body = cls.body as {body?: Node[]} | undefined;
  return (body?.body ?? []).filter(isPrivateKey);
};

/** Every node below `node`, depth first. */
function* walk(node: unknown): Generator<Node> {
  if (Array.isArray(node)) {
    for (const item of node) {
      yield* walk(item);
    }
  } else if (isNode(node)) {
    yield node;
    for (const key in node) {
      if (key !== 'parent') {
        yield* walk(node[key]);
      }
    }
  }
}

/** Names of the slots passed to esbuild's `__privateAdd` helper. */
const addedSlots = (ast: Node): Set<string> => {
  const added = new Set<string>();
  for (const node of walk(ast)) {
    const callee = node.callee as Node | undefined;
    const slot = (node.arguments as Node[] | undefined)?.[1];
    if (
      node.type === 'CallExpression' &&
      callee?.type === 'Identifier' &&
      (callee as unknown as {name: string}).name === '__privateAdd' &&
      slot?.type === 'Identifier'
    ) {
      added.add((slot as unknown as {name: string}).name);
    }
  }
  return added;
};

/** The constructor of an argument-less `new WeakMap()` / `new WeakSet()`. */
const weakCtorOf = (node: Node | undefined): string | undefined => {
  const ctor = (node?.callee as {name?: string} | undefined)?.name;
  const isWeak = ctor === 'WeakMap' || ctor === 'WeakSet';
  return node?.type === 'NewExpression' &&
    isWeak &&
    (node.arguments as unknown[]).length === 0
    ? ctor
    : undefined;
};

/** The `slot = new WeakMap()` / `new WeakSet()` a statement makes, if any. */
const slotAssignment = (
  stmt: Node,
  added: Set<string>
): {name: string; ctor: string; node: Node} | undefined => {
  const expr = stmt.expression as Node | undefined;
  if (stmt.type !== 'ExpressionStatement' || !expr) {
    return undefined;
  }
  const left = expr.left as {type?: string; name?: string} | undefined;
  const right = expr.right as Node | undefined;
  const ctor = weakCtorOf(right);
  if (
    expr.type === 'AssignmentExpression' &&
    expr.operator === '=' &&
    left?.type === 'Identifier' &&
    added.has(left.name!) &&
    ctor &&
    right
  ) {
    return {name: left.name!, ctor, node: right};
  }
  return undefined;
};

/**
 * Top-level `slot = new WeakMap()` / `new WeakSet()` assignments that back
 * private members esbuild lowered, recognised by the slot being passed to
 * its `__privateAdd` helper.
 */
const loweredSlots = (
  ast: Node
): {name: string; ctor: string; node: Node}[] => {
  const added = addedSlots(ast);
  const out: {name: string; ctor: string; node: Node}[] = [];
  for (const stmt of (ast.body as Node[] | undefined) ?? []) {
    const slot = slotAssignment(stmt, added);
    if (slot) {
      out.push(slot);
    }
  }
  return out;
};

/** `obj.#x` / `obj?.#x`. */
const isPrivateAccess = (node: Node): boolean =>
  node.type === 'MemberExpression' &&
  isNode(node.property) &&
  node.property.type === 'PrivateIdentifier';

/** `#x in obj`. */
const isPrivateIn = (node: Node): boolean =>
  node.type === 'BinaryExpression' &&
  isNode(node.left) &&
  node.left.type === 'PrivateIdentifier';

/** The class-name hint `node` passes to the child under `key`. */
const childHintFor = (node: Node, key: string): string | undefined => {
  const nameOf = (v: unknown): string | undefined =>
    isNode(v) && v.type === 'Identifier'
      ? (v as unknown as {name: string}).name
      : undefined;
  switch (node.type) {
    case 'VariableDeclarator':
      return key === 'init' ? nameOf(node.id) : undefined;
    case 'AssignmentExpression':
      return key === 'right' ? nameOf(node.left) : undefined;
    case 'ExportDefaultDeclaration':
      return 'default';
    default:
      return undefined;
  }
};

/**
 * Rewrites native `#private` class members into computed keys backed by
 * `Symbol.for(...)`. The key is stable across re-evaluations of the module,
 * so instances built by an earlier evaluation of a class and methods copied
 * from a later one agree on where private state lives. That is what lets HMR
 * patch a class in place. Private members esbuild already lowered get the
 * same treatment through their slots. Returns `null` when there is nothing to
 * rewrite or the module can't be handled safely.
 */
export const rewritePrivateNames = (
  code: string,
  id: string
): {code: string; map: ReturnType<MagicString['generateMap']>} | null => {
  if (!code.includes('#') && !code.includes('__privateAdd')) {
    return null;
  }
  const [file] = id.split('?', 1);
  let ast: Node;
  try {
    ast = parseAst(code, {sourceType: 'module'}, file) as unknown as Node;
  } catch {
    return null;
  }

  let prefix = '__litPriv';
  while (code.includes(prefix)) {
    prefix += '_';
  }

  const magic = new MagicString(code);
  const decls: string[] = [];
  const classKeyCounts = new Map<string, number>();
  let symbolCount = 0;
  let failed = false;

  const symbolFor = (scope: ClassScope, name: string): string | undefined =>
    scope.names.get(name);

  const resolve = (stack: ClassScope[], name: string): string => {
    for (let i = stack.length - 1; i >= 0; i--) {
      const sym = symbolFor(stack[i], name);
      if (sym !== undefined) {
        return sym;
      }
    }
    failed = true;
    return '';
  };

  const classKeyOf = (cls: Node, hint: string | undefined): string => {
    const cid = cls.id as {name?: string} | null | undefined;
    const base = cid?.name ?? hint ?? 'anonymous';
    const n = classKeyCounts.get(base) ?? 0;
    classKeyCounts.set(base, n + 1);
    return n === 0 ? base : `${base}~${n}`;
  };

  const rewriteMemberAccess = (node: Node, sym: string): void => {
    const prop = node.property as Node;
    // Find the `.` / `?.` token before the property; the object's own end
    // can't be used because a parenthesised object ends before its `)`.
    let i = prop.start - 1;
    while (i >= 0 && /\s/.test(code[i])) {
      i--;
    }
    if (code[i] !== '.') {
      failed = true;
      return;
    }
    const optional = code[i - 1] === '?';
    const from = optional ? i - 1 : i;
    magic.overwrite(from, prop.end, `${optional ? '?.' : ''}[${sym}]`);
  };

  /** Overwrites a private key, led by `;` where ASI could swallow it. */
  const rewriteKey = (member: Node, sym: string): void => {
    const key = member.key as Node;
    // A computed key at the start of a member can continue the previous
    // member's initializer when that one relied on ASI (`a = 1` + newline
    // + `#b`), so lead with an explicit terminator.
    let lead = '';
    if (member.start === key.start) {
      let j = key.start - 1;
      while (j >= 0 && /\s/.test(code[j])) {
        j--;
      }
      if (code[j] !== '{' && code[j] !== ';') {
        lead = ';';
      }
    }
    magic.overwrite(key.start, key.end, `${lead}[${sym}]`);
  };

  /** Declares `member`'s private name in `scope` and rewrites its key. */
  const declareMember = (
    scope: ClassScope,
    member: Node,
    classKey: string
  ): void => {
    const name = (member.key as unknown as {name: string}).name;
    if (!scope.names.has(name)) {
      const ident = `${prefix}${symbolCount++}`;
      scope.names.set(name, ident);
      // The inspector reads instances' private fields by this description
      // (`runtime/inspector/private-fields.ts`); change both together.
      decls.push(
        `const ${ident} = Symbol.for(${JSON.stringify(
          `@oddsquad/vite-plugin-lit#private:${file}:${classKey}#${name}`
        )});`
      );
    }
    if ((member.decorators as unknown[] | undefined)?.length) {
      // A decorator directly before a computed key would parse as an
      // index access on the decorator expression.
      failed = true;
      return;
    }
    rewriteKey(member, scope.names.get(name)!);
  };

  const visitClass = (
    node: Node,
    stack: ClassScope[],
    hint: string | undefined
  ): void => {
    const members = declaredNames(node);
    // Decorators are outside the class body's private-name scope.
    visit(node.decorators, stack, undefined);
    visit(node.superClass, stack, undefined);
    if (members.length === 0) {
      visit(node.body, stack, undefined);
      return;
    }
    const classKey = classKeyOf(node, hint);
    const scope: ClassScope = {names: new Map()};
    for (const member of members) {
      declareMember(scope, member, classKey);
      if (failed) {
        return;
      }
    }
    // Members: keys of non-private members and all values live in scope.
    const inner = [...stack, scope];
    const body = node.body as Node;
    for (const member of (body.body as Node[]) ?? []) {
      for (const k in member) {
        if (k === 'parent' || (k === 'key' && isPrivateKey(member))) {
          continue;
        }
        visit(member[k], inner, undefined);
      }
    }
  };

  /** `obj.#x` / `obj?.#x`. */
  const visitPrivateAccess = (node: Node, stack: ClassScope[]): void => {
    const sym = resolve(
      stack,
      (node.property as unknown as {name: string}).name
    );
    if (failed) {
      return;
    }
    rewriteMemberAccess(node, sym);
    visit(node.object, stack, undefined);
  };

  /** `#x in obj`. */
  const visitPrivateIn = (node: Node, stack: ClassScope[]): void => {
    const left = node.left as Node;
    const sym = resolve(stack, (left as unknown as {name: string}).name);
    if (failed) {
      return;
    }
    magic.overwrite(left.start, left.end, sym);
    visit(node.right, stack, undefined);
  };

  const visitNode = (
    node: Node,
    stack: ClassScope[],
    hint: string | undefined
  ): void => {
    if (node.type === 'ClassDeclaration' || node.type === 'ClassExpression') {
      visitClass(node, stack, hint);
    } else if (isPrivateAccess(node)) {
      visitPrivateAccess(node, stack);
    } else if (isPrivateIn(node)) {
      visitPrivateIn(node, stack);
    } else {
      for (const key in node) {
        if (key !== 'parent') {
          visit(node[key], stack, childHintFor(node, key));
        }
      }
    }
  };

  const visit = (
    node: unknown,
    stack: ClassScope[],
    hint: string | undefined
  ): void => {
    if (failed) {
      return;
    }
    if (Array.isArray(node)) {
      for (const item of node) {
        visit(item, stack, hint);
      }
    } else if (isNode(node)) {
      visitNode(node, stack, hint);
    }
  };

  visit(ast, [], undefined);
  if (failed) {
    return null;
  }

  // esbuild (Vite 7's TS step) lowers `#private` members to module-level
  // WeakMap/WeakSet slots when it also lowers the class's fields, as it does
  // with `useDefineForClassFields: false`. Each evaluation would make fresh
  // slots that earlier instances aren't in, so fetch them from a registry
  // keyed by the slot's binding instead.
  const slots = loweredSlots(ast);
  if (slots.length > 0) {
    const registry = `${prefix}Slot`;
    decls.push(
      `const ${registry} = (k, C) => { const m = globalThis[Symbol.for("@oddsquad/vite-plugin-lit#private")] ??= new Map(); let s = m.get(k); if (!s) m.set(k, (s = new C())); return s; };`
    );
    for (const {name, ctor, node} of slots) {
      magic.overwrite(
        node.start,
        node.end,
        `${registry}(${JSON.stringify(
          `@oddsquad/vite-plugin-lit#private:${file}:${name}`
        )}, ${ctor})`
      );
    }
  }
  if (decls.length === 0) {
    return null;
  }

  const header = `${decls.join('')}\n`;
  if (code.startsWith('#!')) {
    const nl = code.indexOf('\n');
    if (nl === -1) {
      return null;
    }
    magic.appendLeft(nl + 1, header);
  } else {
    magic.prepend(header);
  }
  return {code: magic.toString(), map: magic.generateMap({hires: 'boundary'})};
};

/**
 * Dev-only transform that swaps native `#private` class members for stable
 * symbol keys so HMR's in-place class patching keeps working. See
 * {@link rewritePrivateNames}. Gates itself on the context's options, which
 * are only resolved against the loaded env after the plugin array is built.
 */
export const litPrivateFields = (ctx: OptionsContext): Plugin => ({
  name: 'lit-private-fields',
  apply: 'serve',
  transform(code, id, options) {
    const {hmrEnabled, privateFields} = ctx.get();
    if (!hmrEnabled || !privateFields) {
      return null;
    }
    if (!ctx.shouldTransform(id, options)) {
      return null;
    }
    return rewritePrivateNames(code, id);
  },
});
